import React from 'react';
import PropTypes from 'prop-types';
import config from 'config';
import * as mixpanelTracker from 'app-lib/mixpanel/mixpanelTracker';
import * as detectBrowser from 'app-lib/helpers/browser-detect-helper';
import { appLogger } from 'app-lib/helpers/logging';
import * as eventCaptureService from 'app-lib/events/event-capture-service';

const logger = appLogger.create('video-player');
const timeUpdateEvent = 'timeUpdateEvent';
const seekEvent = 'seekEvent';
const completeEvent = 'completeEvent';
const pauseEvent = 'pauseEvent';
const readyEvent = 'readyEvent';

export class VideoPlayer extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            jwPlayerReady: false,
            bcPlayerReady: false
        };
    }

    componentDidMount() {
        if (this.props.jwUrl && !this.state.jwPlayerReady) {
            this.setJwPlayer(this.props.jwUrl);
        } else if (!this.state.bcPlayerReady) {
            this.setBcPlayer();
        }
        try {
            sessionStorage.setItem('analyticsCounter', 1);
        } catch (e) {
            console.log('error', e);
        }
    }

    sendEvent(eventName, defaultEvent = {}) {
        // add event identifier attributes
        const { contentKey, title, url, channelName, channelKey, identityKey, email } = this.props;
        const eventBody = Object.assign({}, defaultEvent, {
            contentKey,
            title,
            url,
            channelKey,
            channelName,
            identityKey,
            email
        });
        // send
        eventCaptureService.send(eventName, eventBody);
    }

    logAttributes(errorMsg, error) {
        logger.error(errorMsg, error);
    }

    setBcPlayer() {
        const videoElement = this.bcPlayer;
        const mixpanelAttributes = {
            contentKey: this.props.contentKey,
            localTimestamp: Date.now()
        };
        (this.props.contentRecommendedData) ? mixpanelAttributes.recommendationPayload = this.props.contentRecommendedData : null;
        // register for native video events
        if (videoElement) {
            videoElement.onpause = () => {
                mixpanelTracker.track(`${mixpanelTracker.wnPage}-${mixpanelTracker.bcplayerAnalyticsPause}`, mixpanelAttributes);
                this.sendEvent(pauseEvent);
            };

            videoElement.addEventListener('timeupdate', (event) => {
                const { currentTime, duration, hidden } = event.target;

                let counter;
                try {
                    counter = sessionStorage.getItem('analyticsCounter');
                } catch (e) {
                    console.log('error', e);
                }

                mixpanelAttributes.progress = {currentTime, duration, hidden};
                const pcntProgress = Math.floor((currentTime / duration) * 100);
                if (pcntProgress % 5 === 0 && parseInt(counter, 10) !== pcntProgress) {
                    try {
                        sessionStorage.setItem('analyticsCounter', pcntProgress);
                    } catch (e) {
                        console.log('error', e);
                    }
                    mixpanelTracker.track(`${mixpanelTracker.wnPage}-${mixpanelTracker.bcplayerAnalyticsProgress}`, mixpanelAttributes);
                    this.sendEvent(timeUpdateEvent, {progress: pcntProgress});
                }
            });
            videoElement.addEventListener('seeked', (event) => {
                const { currentTime, duration, hidden } = event.target;
                mixpanelAttributes.progress = {currentTime, duration, hidden};
                mixpanelTracker.track(`${mixpanelTracker.wnPage}-${mixpanelTracker.bcplayerAnalyticsSeeked}`, mixpanelAttributes);
                this.sendEvent(seekEvent, {seek: {currentTime, duration}});
            });
            videoElement.addEventListener('ended', () => {
                mixpanelTracker.track(`${mixpanelTracker.wnPage}-${mixpanelTracker.bcplayerAnalyticsComplete}`, mixpanelAttributes);
                this.sendEvent(completeEvent);
            });
        }

        if (videoElement && window.videojs && window.bc) {
            window.bc(videoElement);
            window.videojs('bcPlayer').ready()
           .on('fullscreenchange', (msg) => {
               mixpanelTracker.track(`${mixpanelTracker.wnPage}-${mixpanelTracker.bcplayerAnalyticsFullscreen}`, mixpanelAttributes);
           });
            this.setState({bcPlayerReady: true});
            this.sendEvent(readyEvent);
            mixpanelTracker.track(`${mixpanelTracker.wnPage}-${mixpanelTracker.bcLoaded}`, mixpanelAttributes);
            window.videojs('bcPlayer').on('error', (msg) => {
                this.logAttributes('Brightcove Error occurred while loading video', {msg});
            });
            window.videojs('bcPlayer').one('bc-catalog-error', () => {
                const myPlayer = this;
                let specificError;
                const self = this;
                if (myPlayer.catalog.error !== undefined) {
                    specificError = myPlayer.catalog.error.data[0];
                    self.logAttributes('Brightcove Catalog Error', {specificError});
                }
            });
        }
    }

    enableAutoplay() {
        const element = document.getElementById('bcPlayer');
        if (element && window.videojs) {
            const self = this;
            self.playerInstance = window.videojs('bcPlayer');
            self.playerInstance
                .ready()
                .on('loadstart', (msg) => {
                    // Special case for mobile browsers to enable autoplay https://webkit.org/blog/6784/new-video-policies-for-ios/
                    // https://support.brightcove.com/ios-and-brightcove-player
                    if (detectBrowser.isMobile()) {
                        self.playerInstance.play();
                        self.playerInstance.muted(false);
                    }
                });
        }
    }

    setJwPlayer(recording) {
        const element = document.getElementById('jwPlayer');
        if (typeof(element) !== 'undefined' && element !== null && window.jwplayer) {
            const self = this;
            self.playerInstance = window.jwplayer('jwPlayer');
            const mixpanelAttributes = {
                contentKey: this.props.contentKey,
                localTimestamp: Date.now()
            };
            (this.props.contentRecommendedData) ? mixpanelAttributes.recommendationPayload = this.props.contentRecommendedData : null;
            (this.props.searchId) ? mixpanelAttributes.search_id = this.props.searchId : null;
            // setup and analytics
            self.playerInstance
            .setup({
                type: 'mp4',
                file: recording,
                width: '100%'
            }).on('ready', (msg) => {
                if (detectBrowser.isMobile()) {
                    self.playerInstance.setMute(false);
                }
            })
            .on('setupError', (msg) => {
                self.logAttributes('jwplayer setup error', {msg});
            })
            .on('error', (msg) => {
                const data = {};
                data.contentKey = self.props.contentKey;
                data.recording = recording;
                data.msg = msg;
                self.logAttributes('jwplayer playback error', data);
            })
            .on('time', (msg) => {
                let counter;
                try {
                    counter = sessionStorage.getItem('analyticsCounter');
                } catch (e) {
                    console.log('error', e);
                }
                mixpanelAttributes.progress = msg;
                const pcntProgress = Math.floor((msg.position / msg.duration) * 100);
                if (pcntProgress % 5 === 0 && parseInt(counter, 10) !== pcntProgress) {
                    try {
                        sessionStorage.setItem('analyticsCounter', pcntProgress);
                    } catch (e) {
                        console.log('error', e);
                    }
                    mixpanelTracker.track(`${mixpanelTracker.wnPage}-${mixpanelTracker.jwplayerAnalyticsProgress}`, mixpanelAttributes);
                    this.sendEvent(timeUpdateEvent, {progress: pcntProgress});
                }
            })
            .on('seek', (msg) => {
                mixpanelAttributes.seek = msg;
                mixpanelTracker.track(`${mixpanelTracker.wnPage}-${mixpanelTracker.jwplayerAnalyticsSeek}`, mixpanelAttributes);
                this.sendEvent(seekEvent, {seek: msg});
            })
            .on('pause', (msg) => {
                mixpanelAttributes.paused = msg;
                mixpanelTracker.track(`${mixpanelTracker.wnPage}-${mixpanelTracker.jwplayerAnalyticsPause}`, mixpanelAttributes);
                this.sendEvent(pauseEvent, {paused: msg});
            })
            .on('fullscreen', (msg) => {
                mixpanelTracker.track(`${mixpanelTracker.wnPage}-${mixpanelTracker.jwplayerAnalyticsFullscreen}`, mixpanelAttributes);
            })
            .on('complete', (msg) => {
                mixpanelTracker.track(`${mixpanelTracker.wnPage}-${mixpanelTracker.jwplayerAnalyticsComplete}`, mixpanelAttributes);
                this.sendEvent(completeEvent);
            });

            this.sendEvent(readyEvent);
            this.setState({jwPlayerReady: true});
            mixpanelTracker.track(`${mixpanelTracker.wnPage}-${mixpanelTracker.jwLoaded}`, mixpanelAttributes);
        }
    }

    render() {
        if (this.props.jwUrl) {
            return <div className="wn_content-video"><div id="jwPlayer"></div></div>;
        } else if (this.props.bcID) {
            if (!window.videojs) {
                const script = document.createElement('script');
                document.body.appendChild(script);
                script.onload = this.enableAutoplay;
                script.src = `//players.brightcove.net/${config.brightcove.accountId}/${config.brightcove.playerId}_default/index.min.js`;
                script.async = true;

                const mixpanelAttributes = {
                    contentKey: this.props.contentKey,
                    localTimestamp: Date.now()
                };
                (this.props.contentRecommendedData) ? mixpanelAttributes.recommendationPayload = this.props.contentRecommendedData : null;
                (this.props.searchId) ? mixpanelAttributes.search_id = this.props.searchId : null;
                this.sendEvent(readyEvent);
                mixpanelTracker.track(`${mixpanelTracker.wnPage}-${mixpanelTracker.bcLoaded}`, mixpanelAttributes);
            }
            return (
                <div className="wn_content-video">
                    <video
                      id="bcPlayer"
                      ref={(bcPlayer => (this.bcPlayer = bcPlayer))}
                      data-account={config.brightcove.accountId}
                      data-video-id={`ref:${this.props.bcID}`}
                      data-player={config.brightcove.playerId}
                      data-embed="default"
                      data-application-id
                      className="video-js"
                      controls
                      width="100%"
                      height="100%"
                    >
                    </video>
                </div>
            );
        }
        return null;
    }
}

VideoPlayer.defaultProps = {
    contentKey: '',
    title: '',
    url: '',
    contentName: '',
    channelKey: '',
    identityKey: '',
    email: ''
};

VideoPlayer.propTypes = {
    title: PropTypes.string,
    url: PropTypes.string,
    channelKey: PropTypes.string,
    channelName: PropTypes.string,
    identityKey: PropTypes.string,
    email: PropTypes.string,
    bcID: PropTypes.string,
    jwUrl: PropTypes.string,
    contentKey: PropTypes.string,
    contentRecommendedData: PropTypes.string,
    searchId: PropTypes.string
};



// WEBPACK FOOTER //
// ./src/components/SharedComponents/video-player.js